import { useEffect } from "react";

type Sparkle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
};

const COLORS = ["#c084fc", "#9b3df5", "#e9d5ff", "#ff5cf8"];

/** Canvas-based purple sparkle trail that follows the cursor. Pixel-square sparkles, low cost. */
export function useCursorSparkles() {
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = document.createElement("canvas");
    canvas.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 9996;
      width: 100vw; height: 100vh; mix-blend-mode: screen;
    `;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d")!;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const sparkles: Sparkle[] = [];
    let lastX = 0,
      lastY = 0,
      moveSpeed = 0;

    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      moveSpeed = Math.min(Math.hypot(dx, dy), 60);
      lastX = e.clientX;
      lastY = e.clientY;

      // Spawn proportional to speed, but always at least one occasionally.
      const count = Math.max(1, Math.floor(moveSpeed / 8));
      for (let i = 0; i < count; i++) {
        sparkles.push({
          x: e.clientX + (Math.random() - 0.5) * 8,
          y: e.clientY + (Math.random() - 0.5) * 8,
          vx: (Math.random() - 0.5) * 1.2,
          vy: (Math.random() - 0.5) * 1.2 - 0.4,
          life: 0,
          maxLife: 30 + Math.random() * 30,
          size: Math.random() < 0.7 ? 2 : 3,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
        });
      }
      // Cap pool size.
      if (sparkles.length > 220) sparkles.splice(0, sparkles.length - 220);
    };
    window.addEventListener("mousemove", onMove);

    let raf = 0;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = sparkles.length - 1; i >= 0; i--) {
        const s = sparkles[i];
        s.life++;
        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.02; // tiny gravity
        if (s.life >= s.maxLife) {
          sparkles.splice(i, 1);
          continue;
        }
        const alpha = 1 - s.life / s.maxLife;
        ctx.fillStyle = s.color;
        ctx.globalAlpha = alpha;
        // Pixel-square sparkles
        ctx.fillRect(Math.round(s.x), Math.round(s.y), s.size, s.size);
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
      canvas.remove();
    };
  }, []);
}
